import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Drag-and-drop board for an onboarding template's groups + steps.
//
// Steps can be reordered within a group and dragged between groups (including an
// "Ungrouped" bucket). On drop the whole board is diffed and only the moved
// steps are persisted (new Order + new OnboardingChecklistTemplateGroup_Id) via
// onPersist. Mirrors the @dnd-kit multi-container pattern used by the Tasks
// Status Kanban, but as a simple vertical stack of groups.

const UNGROUPED = "__ungrouped__";

const parseId = (domId) => {
  const s = String(domId);
  const i = s.indexOf(":");
  return { type: s.slice(0, i), raw: s.slice(i + 1) };
};

// One draggable step row. The WHOLE card is the drag handle (more discoverable
// than a tiny grip); the action buttons/link stop pointer propagation so a
// click on them never starts a drag and they stay clickable.
function SortableStep({
  step,
  index,
  onEdit,
  onDelete,
  moveTargets,
  currentKey,
  onMove,
  renderStep,
  disabled,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `step:${step.Id}`, disabled });

  // When a custom renderer is supplied (the rich onboarding row), the parent
  // owns the card layout — we just give it a drag handle + the move menu and
  // wrap it in the sortable node.
  if (renderStep) {
    return (
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.4 : 1,
        }}
      >
        {renderStep(step, {
          handleProps: disabled
            ? null
            : {
                ...attributes,
                ...listeners,
                style: { cursor: "grab", touchAction: "none" },
              },
          isDragging,
          moveTargets,
          currentKey,
          onMove,
        })}
      </div>
    );
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    border: "1px solid var(--vz-border-color, #e9ebec)",
    cursor: disabled ? "default" : "grab",
    touchAction: "none",
  };

  // Keep the action controls clickable: swallow pointerdown so the drag sensor
  // never engages when the user is aiming for a button/link.
  const stop = { onPointerDown: (e) => e.stopPropagation() };
  const dragProps = disabled ? {} : { ...attributes, ...listeners };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-start p-2 rounded bg-white"
      {...dragProps}
    >
      <span
        className="text-secondary me-2 mt-1 flex-shrink-0 d-inline-flex"
        title="Drag to reorder or move to another group"
      >
        <i className="ri-more-2-fill" style={{ fontSize: 18 }} />
        <i className="ri-more-2-fill" style={{ fontSize: 18, marginLeft: -8 }} />
      </span>
      <div
        className="d-flex align-items-center justify-content-center rounded-circle bg-light text-muted me-2 flex-shrink-0"
        style={{ width: 28, height: 28 }}
      >
        {step.Order ?? index + 1}
      </div>
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-medium">{step.Title}</span>
          {Number(step.FlagRequired) === 1 ? (
            <Badge color="danger-subtle" className="text-danger">
              Required
            </Badge>
          ) : (
            <Badge color="light" className="text-muted">
              Optional
            </Badge>
          )}
        </div>
        {step.Description ? (
          <div className="text-muted small mt-1">{step.Description}</div>
        ) : null}
      </div>
      <div className="hstack gap-1 ms-2" {...stop}>
        {step.LinkUrl ? (
          <a
            href={step.LinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-soft-info btn-sm"
            title={step.LinkLabel || "Open link"}
          >
            <i className="ri-external-link-line align-bottom me-1" />
            {step.LinkLabel || "Open"}
          </a>
        ) : null}
        {moveTargets && moveTargets.length > 1 ? (
          <UncontrolledDropdown>
            <DropdownToggle
              color="light"
              size="sm"
              caret
              title="Move to another group"
            >
              <i className="ri-folder-transfer-line align-bottom" />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem header>Move to</DropdownItem>
              {moveTargets
                .filter((t) => t.key !== currentKey)
                .map((t) => (
                  <DropdownItem key={t.key} onClick={() => onMove(step, t.key)}>
                    {t.name}
                  </DropdownItem>
                ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : null}
        <Button color="light" size="sm" onClick={() => onEdit(step)}>
          <i className="ri-pencil-line align-bottom" />
        </Button>
        <Button color="light" size="sm" onClick={() => onDelete(step)}>
          <i className="ri-delete-bin-line align-bottom text-danger" />
        </Button>
      </div>
    </div>
  );
}

// A group container (droppable so empty groups still accept a drop).
function GroupContainer({
  containerKey,
  group,
  stepIds,
  stepsById,
  onCreateStep,
  onEditGroup,
  onDeleteGroup,
  onEditStep,
  onDeleteStep,
  moveTargets,
  onMove,
  collapsed,
  onToggleCollapse,
  renderStep,
  renderGroupMeta,
  disabled,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `container:${containerKey}` });
  const stepObjs = stepIds.map((id) => stepsById[id]).filter(Boolean);

  return (
    <div>
      {/* Group header — brand gradient bar */}
      <div
        className="d-flex align-items-center justify-content-between rounded px-3 py-2 mb-2 gap-2 text-white"
        style={{
          background:
            "linear-gradient(120deg, #4f46e5 0%, #6d28d9 46%, #0ea5e9 100%)",
        }}
      >
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 text-white flex-shrink-0"
          onClick={() => onToggleCollapse(containerKey)}
        >
          <i
            className={collapsed ? "ri-arrow-right-s-line" : "ri-arrow-down-s-line"}
          />
          <i className="ri-folder-2-line text-white-50" />
          <span className="fw-semibold">{group ? group.Name : "Ungrouped"}</span>
        </button>
        <span className="d-flex align-items-center gap-2 ms-auto">
          {renderGroupMeta ? (
            renderGroupMeta(group, stepObjs)
          ) : (
            <span className="text-white-50 small">
              {stepIds.length} step{stepIds.length === 1 ? "" : "s"}
            </span>
          )}
          {group ? (
            <span className="hstack gap-1">
              <Button
                color="light"
                size="sm"
                title="Add step to this group"
                onClick={() => onCreateStep(group.Id)}
              >
                <i className="ri-add-line align-bottom" />
              </Button>
              <Button
                color="light"
                size="sm"
                title="Edit group"
                onClick={() => onEditGroup(group)}
              >
                <i className="ri-pencil-line align-bottom" />
              </Button>
              <Button
                color="light"
                size="sm"
                title="Delete group"
                onClick={() => onDeleteGroup(group)}
              >
                <i className="ri-delete-bin-line align-bottom text-danger" />
              </Button>
            </span>
          ) : null}
        </span>
      </div>

      {/* Droppable step list (hidden when collapsed) */}
      {collapsed ? null : (
        <SortableContext
          items={stepIds.map((id) => `step:${id}`)}
          strategy={verticalListSortingStrategy}
        >
          <div
            ref={setNodeRef}
            className="vstack gap-2 pb-1"
            style={{
              minHeight: 44,
              borderRadius: 6,
              outline: isOver ? "2px dashed var(--vz-primary, #405189)" : "none",
              outlineOffset: 2,
            }}
          >
            {stepIds.length === 0 ? (
              <div className="text-muted small px-2 py-2">
                Drag steps here, or add one.
              </div>
            ) : (
              stepIds.map((id, idx) => (
                <SortableStep
                  key={id}
                  step={stepsById[id]}
                  index={idx}
                  onEdit={onEditStep}
                  onDelete={onDeleteStep}
                  moveTargets={moveTargets}
                  currentKey={containerKey}
                  onMove={onMove}
                  renderStep={renderStep}
                  disabled={disabled}
                />
              ))
            )}
          </div>
        </SortableContext>
      )}
    </div>
  );
}

export default function OnboardingTemplateStepsBoard({
  groups,
  steps,
  onCreateStep,
  onEditStep,
  onDeleteStep,
  onEditGroup,
  onDeleteGroup,
  onPersist,
  // Which FK on a step points at its group. Defaults to the template field;
  // the live (instance) board passes "CustomerOnboardingGroup_Id".
  groupIdField = "OnboardingChecklistTemplateGroup_Id",
  // Optional custom row renderer: renderStep(step, { handleProps, moveTargets,
  // currentKey, onMove, isDragging }) — used to render the rich onboarding row.
  renderStep,
  // Optional right-side group-header content: renderGroupMeta(group, stepObjs).
  renderGroupMeta,
  // Disable dragging (e.g. while a filter is active) — cards still render.
  disabled = false,
}) {
  // board = { containers: [key...], items: { key: [stepId...] }, stepsById }.
  // boardRef mirrors it synchronously so drag handlers always see the latest.
  const [board, setBoard] = useState({ containers: [], items: {}, stepsById: {} });
  const boardRef = useRef(board);
  const [activeStepId, setActiveStepId] = useState(null);
  const [collapsed, setCollapsed] = useState({}); // { [containerKey]: true }

  useEffect(() => {
    const containers = groups.map((g) => String(g.Id));
    containers.push(UNGROUPED);
    const items = Object.fromEntries(containers.map((c) => [c, []]));
    const stepsById = {};
    for (const s of steps) {
      // Ids come back from the API as strings — keep everything string so the
      // includes()/indexOf() comparisons in the drag handlers actually match.
      const sid = String(s.Id);
      stepsById[sid] = s;
      const key = s[groupIdField] ? String(s[groupIdField]) : UNGROUPED;
      (items[key] || items[UNGROUPED]).push(sid);
    }
    const next = { containers, items, stepsById };
    boardRef.current = next;
    setBoard(next);
  }, [groups, steps, groupIdField]);

  // boardRef is the synchronous source of truth; setBoardSync mirrors it into
  // state for rendering. (React does NOT run setState updaters synchronously,
  // so we cannot read back an updated board inside the same event — hence the
  // ref.)
  const setBoardSync = (next) => {
    boardRef.current = next;
    setBoard(next);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const groupByKey = {};
  for (const g of groups) groupByKey[String(g.Id)] = g;

  const containerKeyOf = (b, domId) => {
    const { type, raw } = parseId(domId);
    if (type === "container") return raw;
    return b.containers.find((c) => b.items[c].includes(raw));
  };

  const onDragStart = ({ active }) =>
    setActiveStepId(parseId(active.id).raw);

  // Reorder LIVE as you hover — both within a group and across groups — so the
  // board state always mirrors what's on screen. onDragEnd then just persists.
  // All ids are STRINGS ("step:5" -> "5") to match the API's string Ids.
  const onDragOver = ({ active, over }) => {
    if (!over) return;
    const prev = boardRef.current;
    const activeStep = parseId(active.id).raw;
    const activeKey = prev.containers.find((c) =>
      prev.items[c].includes(activeStep)
    );
    const overKey = containerKeyOf(prev, over.id);
    if (!activeKey || !overKey) return;

    const { type, raw } = parseId(over.id);

    // Same group: move the item to the hovered slot.
    if (activeKey === overKey) {
      const list = prev.items[activeKey];
      const oldIndex = list.indexOf(activeStep);
      const newIndex =
        type === "container" ? list.length - 1 : list.indexOf(raw);
      if (newIndex < 0 || newIndex === oldIndex) return;
      setBoardSync({
        ...prev,
        items: {
          ...prev.items,
          [activeKey]: arrayMove(list, oldIndex, newIndex),
        },
      });
      return;
    }

    // Different group: pull it out of its group and drop it at the hovered slot.
    const overList = prev.items[overKey];
    let newIndex = overList.length;
    if (type === "step") {
      const overIndex = overList.indexOf(raw);
      if (overIndex >= 0) newIndex = overIndex;
    }
    setBoardSync({
      ...prev,
      items: {
        ...prev.items,
        [activeKey]: prev.items[activeKey].filter((id) => id !== activeStep),
        [overKey]: [
          ...overList.slice(0, newIndex),
          activeStep,
          ...overList.slice(newIndex),
        ],
      },
    });
  };

  // Diff the board against each step's original Order/group and persist moves.
  const persistBoard = (next) => {
    const rows = [];
    for (const ck of next.containers) {
      const groupId = ck === UNGROUPED ? null : Number(ck);
      next.items[ck].forEach((stepId, idx) => {
        const original = next.stepsById[stepId];
        const newOrder = (idx + 1) * 10;
        const origGroup = original?.[groupIdField]
          ? Number(original[groupIdField])
          : null;
        if (Number(original?.Order) !== newOrder || origGroup !== groupId) {
          rows.push({
            Id: stepId,
            Order: newOrder,
            [groupIdField]: groupId,
          });
        }
      });
    }
    if (rows.length) onPersist(rows);
  };

  const onDragEnd = () => {
    setActiveStepId(null);
    // The board already reflects the final arrangement (onDragOver); persist it.
    persistBoard(boardRef.current);
  };

  // Move a step to another group via the card's "Move to" menu (a scroll-free
  // alternative to dragging it across a long list). Appends to the target group.
  const moveStepToContainer = (step, targetKey) => {
    const stepId = String(step.Id);
    const prev = boardRef.current;
    const fromKey = prev.containers.find((c) => prev.items[c].includes(stepId));
    if (!fromKey || fromKey === targetKey) return;
    const next = {
      ...prev,
      items: {
        ...prev.items,
        [fromKey]: prev.items[fromKey].filter((id) => id !== stepId),
        [targetKey]: [...prev.items[targetKey], stepId],
      },
    };
    setBoardSync(next);
    persistBoard(next);
  };

  const toggleCollapse = (key) =>
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  const setAllCollapsed = (value) =>
    setCollapsed(
      value
        ? Object.fromEntries(board.containers.map((k) => [k, true]))
        : {}
    );

  // Options for the per-card "Move to" menu: every group + Ungrouped.
  const moveTargets = board.containers.map((key) => ({
    key,
    name: key === UNGROUPED ? "Ungrouped" : groupByKey[key]?.Name || "Group",
  }));

  const allCollapsed =
    board.containers.length > 0 &&
    board.containers.every((k) => collapsed[k]);

  const activeStep = activeStepId ? board.stepsById[activeStepId] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {board.containers.length > 1 ? (
        <div className="d-flex justify-content-end mb-2">
          <Button
            color="link"
            size="sm"
            className="p-0"
            onClick={() => setAllCollapsed(!allCollapsed)}
          >
            <i
              className={`align-bottom me-1 ${
                allCollapsed ? "ri-expand-up-down-line" : "ri-collapse-diagonal-line"
              }`}
            />
            {allCollapsed ? "Expand all" : "Collapse all"}
          </Button>
        </div>
      ) : null}

      <div className="vstack gap-3">
        {board.containers.map((key) => (
          <GroupContainer
            key={key}
            containerKey={key}
            group={key === UNGROUPED ? null : groupByKey[key]}
            stepIds={board.items[key] || []}
            stepsById={board.stepsById}
            onCreateStep={onCreateStep}
            onEditGroup={onEditGroup}
            onDeleteGroup={onDeleteGroup}
            onEditStep={onEditStep}
            onDeleteStep={onDeleteStep}
            moveTargets={moveTargets}
            onMove={moveStepToContainer}
            collapsed={!!collapsed[key]}
            onToggleCollapse={toggleCollapse}
            renderStep={renderStep}
            renderGroupMeta={renderGroupMeta}
            disabled={disabled}
          />
        ))}
      </div>

      <DragOverlay>
        {activeStep ? (
          <div
            className="d-flex align-items-center p-2 rounded bg-white shadow"
            style={{ border: "1px solid var(--vz-border-color, #e9ebec)" }}
          >
            <i className="ri-more-2-fill me-2 text-secondary" />
            <span className="fw-medium">{activeStep.Title}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
